/**
 * Layout strategy (cycle-safe)
 * ----------------------------
 * We need a vertical "family tree" where parents appear above children.
 * However, our data is a directed graph and may contain cycles.
 *
 * If we tried a naive "longest path" depth calculation on a cyclic graph,
 * we could infinite-loop. Instead we:
 *
 * 1) Compute SCCs (strongly connected components) over parent->child edges.
 *    - Any cycle becomes a single SCC "super-node"
 * 2) Condense SCCs into a DAG and compute depths on that DAG.
 *    - Depth is well-defined and cycle-safe.
 * 3) Assign every character the depth of its SCC.
 *
 * Inside an SCC, strict parent-above-child ordering is impossible by definition,
 * so nodes may share a level. Edges that go "up" or "sideways" are rendered as
 * wrap-around connectors in SVG.
 */

function kosarajuScc(nodeIds, getNeighbors, getReverseNeighbors) {
  /**
   * Iterative Kosaraju SCC:
   * - 1st pass: compute finish order on original graph
   * - 2nd pass: DFS on reversed graph in reverse finish order
   *
   * This avoids recursion depth issues for large graphs.
   */
  const visited = new Set();
  const order = [];

  for (let i = 0; i < nodeIds.length; i += 1) {
    const start = nodeIds[i];
    if (!visited.has(start)) {
      // Stack frames: { id, idx, neighbors }
      const stack = [{ id: start, idx: 0, neighbors: getNeighbors(start) }];
      visited.add(start);

      while (stack.length) {
        const top = stack[stack.length - 1];
        if (top.idx >= top.neighbors.length) {
          order.push(top.id);
          stack.pop();
        } else {
          const next = top.neighbors[top.idx];
          top.idx += 1;
          if (!visited.has(next)) {
            visited.add(next);
            stack.push({ id: next, idx: 0, neighbors: getNeighbors(next) });
          }
        }
      }
    }
  }

  const visited2 = new Set();
  const sccs = [];

  for (let i = order.length - 1; i >= 0; i -= 1) {
    const start = order[i];
    if (!visited2.has(start)) {
      const component = [];
      const stack = [start];
      visited2.add(start);

      while (stack.length) {
        const v = stack.pop();
        component.push(v);
        const rev = getReverseNeighbors(v);
        for (let j = 0; j < rev.length; j += 1) {
          const w = rev[j];
          if (!visited2.has(w)) {
            visited2.add(w);
            stack.push(w);
          }
        }
      }

      sccs.push(component);
    }
  }

  return sccs;
}

function safeGet(map, key) {
  return map && map.has(key) ? map.get(key) : undefined;
}

/**
 * @param {{
 *  nodeIds: string[],
 *  parentToChildren: Map<string, Set<string>>,
 *  childToParents: Map<string, Set<string>>,
 *  nodesById: Map<string, Object>
 * }} graph
 * @param {{
 *  nodeWidth?: number,
 *  nodeHeight?: number,
 *  horizontalGap?: number,
 *  verticalGap?: number,
 *  padding?: number
 * }} options
 */
function layoutFamilyGraph(graph, options = {}) {
  const {
    nodeWidth = 160,
    nodeHeight = 56,
    horizontalGap = 40,
    verticalGap = 90,
    padding = 40,
  } = options;

  const nodeIds = Array.isArray(graph?.nodeIds) ? graph.nodeIds : [];
  const parentToChildren = graph?.parentToChildren || new Map();
  const childToParents = graph?.childToParents || new Map();
  const nodesById = graph?.nodesById || new Map();

  const neighbors = (id) => Array.from(safeGet(parentToChildren, id) || []);
  const reverseNeighbors = (id) => Array.from(safeGet(childToParents, id) || []);
  const sccs = kosarajuScc(nodeIds, neighbors, reverseNeighbors);

  // Map node -> component id
  const compByNode = new Map();
  sccs.forEach((comp, idx) => {
    comp.forEach((id) => compByNode.set(id, idx));
  });

  // Build condensed DAG: comp -> comps (parent-child edges only)
  const compCount = sccs.length;
  const compEdges = Array.from({ length: compCount }, () => new Set());
  const indegree = new Array(compCount).fill(0);

  nodeIds.forEach((fromId) => {
    const fromComp = compByNode.get(fromId);
    const children = neighbors(fromId);
    children.forEach((toId) => {
      const toComp = compByNode.get(toId);
      if (fromComp === toComp) return;
      if (!compEdges[fromComp].has(toComp)) {
        compEdges[fromComp].add(toComp);
        indegree[toComp] += 1;
      }
    });
  });

  // Kahn topo order on SCC DAG + longest-path depth
  const queue = [];
  for (let i = 0; i < compCount; i += 1) {
    if (indegree[i] === 0) queue.push(i);
  }

  const compDepth = new Array(compCount).fill(0);
  const topo = [];
  while (queue.length) {
    const c = queue.shift();
    topo.push(c);
    compEdges[c].forEach((next) => {
      compDepth[next] = Math.max(compDepth[next], compDepth[c] + 1);
      indegree[next] -= 1;
      if (indegree[next] === 0) queue.push(next);
    });
  }

  // If topo didn't include everything, it would imply a cycle in SCC DAG (shouldn't happen),
  // but we still guard by leaving remaining compDepth as 0.

  const depthByNode = new Map();
  nodeIds.forEach((id) => {
    const c = compByNode.get(id);
    depthByNode.set(id, compDepth[c] || 0);
  });

  // Stable ordering within levels (aim to keep siblings near their parents)
  const maxDepth = Math.max(0, ...Array.from(depthByNode.values()));
  const levels = Array.from({ length: maxDepth + 1 }, () => []);
  nodeIds.forEach((id) => {
    const d = depthByNode.get(id) || 0;
    levels[d].push(id);
  });

  const orderByNode = new Map();
  const nameOf = (id) => {
    const n = nodesById.get(id);
    return n && n.name ? String(n.name) : '';
  };

  // Depth 0: alphabetical for stability
  levels[0].sort((a, b) => nameOf(a).localeCompare(nameOf(b)) || a.localeCompare(b));
  levels[0].forEach((id, idx) => orderByNode.set(id, idx));

  // Other depths: group by a "preferred parent" (closest parent), then by name
  for (let d = 1; d < levels.length; d += 1) {
    const ids = levels[d];

    const preferredParent = new Map();
    ids.forEach((id) => {
      const parents = Array.from(safeGet(childToParents, id) || []);
      // Pick the parent with smallest depth; tie-break by name/id for stability.
      let best = null;
      let bestDepth = Number.POSITIVE_INFINITY;
      parents.forEach((p) => {
        const pd = depthByNode.get(p);
        if (pd == null) return;
        if (pd < bestDepth) {
          bestDepth = pd;
          best = p;
        } else if (pd === bestDepth && best) {
          const cmp = nameOf(p).localeCompare(nameOf(best)) || p.localeCompare(best);
          if (cmp < 0) best = p;
        }
      });
      preferredParent.set(id, best);
    });

    ids.sort((a, b) => {
      const pa = preferredParent.get(a);
      const pb = preferredParent.get(b);

      const oa = pa ? (orderByNode.get(pa) ?? 999999) : 999999;
      const ob = pb ? (orderByNode.get(pb) ?? 999999) : 999999;
      if (oa !== ob) return oa - ob;

      // Secondary: stable alphabetical
      return nameOf(a).localeCompare(nameOf(b)) || a.localeCompare(b);
    });

    ids.forEach((id, idx) => orderByNode.set(id, idx));
  }

  // Position nodes
  // depth 0 = roots/parents (should be at top), depth 1+ = children (below)
  const positions = new Map();
  let maxCols = 0;
  levels.forEach((ids) => {
    maxCols = Math.max(maxCols, ids.length);
  });

  const svgWidth =
    padding * 2 + Math.max(1, maxCols) * nodeWidth + Math.max(0, maxCols - 1) * horizontalGap;
  const svgHeight = padding * 2 + (maxDepth + 1) * nodeHeight + Math.max(0, maxDepth) * verticalGap;

  levels.forEach((ids, depth) => {
    ids.forEach((id, idx) => {
      const x = padding + idx * (nodeWidth + horizontalGap);
      // We want depth 0 (parents/roots) at top, higher depths (children) below
      // If parents are currently appearing below, they likely have higher depth values
      // Invert the depth: depth 0 maps to position maxDepth (bottom), maxDepth maps to position 0 (top)
      // This puts nodes with higher depth values at the top
      const y = padding + (maxDepth - depth) * (nodeHeight + verticalGap);
      positions.set(id, {
        id,
        depth,
        x,
        y,
        width: nodeWidth,
        height: nodeHeight,
        cx: x + nodeWidth / 2,
        cy: y + nodeHeight / 2,
        top: y,
        bottom: y + nodeHeight,
        left: x,
        right: x + nodeWidth,
      });
    });
  });

  return {
    positions,
    depthByNode,
    levels,
    svg: {
      width: svgWidth,
      height: svgHeight,
      padding,
      nodeWidth,
      nodeHeight,
      horizontalGap,
      verticalGap,
    },
  };
}

export default layoutFamilyGraph;
